/**
 * The policy document the platform pushes and the workbench drops at
 * `.mojoup/policy.json`. Mirrors `workspacePolicySchema` in
 * `src/control/policyFile.ts` of the AI Workbench (version 1). The
 * `inference` overlay is the workbench's `inferencePolicySchema`, which
 * lives in its LLM layer; here it is carried as an opaque object so this
 * package does not have to track that schema's every field.
 */
import { z } from "zod";
import { microsoft365PolicySchema } from "./microsoft365.js";
import { sensitivityLabelsPolicySchema } from "./sensitivityLabels.js";
import { purviewPolicySchema } from "./purview.js";
import { toolsPolicySchema } from "./tools.js";
import { secretsPolicySchema } from "./secrets.js";
import { cloudAgentsPolicySchema } from "./cloudAgents.js";
import { mediaPolicySchema } from "./media.js";
import { a2aPolicySchema } from "./a2a.js";
import { pullRequestsPolicySchema } from "./pullRequests.js";
import { settingsModeSchema } from "./settingsPolicy.js";
import { policyExceptionSchema } from "./policyExceptions.js";
export const POLICY_VERSION = 1;
const decision = z.enum(["allow", "redact", "block", "ask"]);
export const dlpPolicySchema = z.object({
    classify: z.boolean().default(false),
    /** Microsoft Purview sensitivity labels mapped onto the sensitivity enum, and where each level may go (sensitivityLabels.ts); AIOE publishes it from the tenant's taxonomy. */
    labels: sensitivityLabelsPolicySchema.optional(),
    /** Purview's inline verdict on prompts and requests bound for a hosted model (purview.ts); off unless turned on. */
    purview: purviewPolicySchema.optional(),
    classifier: z
        .object({
        provider: z.string().default("ollama"),
        model: z.string().optional(),
        endpoint: z.string().optional(),
        /** A node (device id) that serves DLP classification for the project. AIOE-first: not yet in the workbench's policyFile.ts. */
        node: z.string().optional(),
        /** Or any assigned node carrying these tags. AIOE-first, as `node`. */
        tags: z.array(z.string()).optional(),
    })
        .prefault({}),
    onSecrets: decision.default("redact"),
    onPii: decision.default("allow"),
    onPromptInjection: z.enum(["allow", "block", "ask"]).default("allow"),
    blockSensitivity: z.array(z.enum(["public", "internal", "confidential", "restricted"])).default(["restricted"]),
    /** When the classifier cannot answer in time (down, model missing, over budget): fail open ("allow", the default), stop and ask, or fail closed ("block"). */
    onUnclassified: z.enum(["allow", "ask", "block"]).default("allow"),
    latencyBudgetMs: z.number().int().min(50).max(5000).default(300),
});
export const approvalsPolicySchema = z.object({
    remoteApprovals: z
        .object({
        low: z.enum(["allow", "deny"]).default("allow"),
        medium: z.enum(["allow", "deny"]).default("allow"),
        high: z.enum(["allow", "deny"]).default("deny"),
    })
        .prefault({}),
    requireHumanReviewLabels: z.array(z.string()).default([]),
    /**
     * Which decisions an agent records need a person's approval before they stand (the workbench's
     * decision register, D118): `wider`, the project's and the organisation's; `all`, every one.
     * Absent or `none`: an agent's decision stands as accepted, and is audited either way.
     */
    decisionApproval: z.enum(["none", "wider", "all"]).optional(),
});
/** Mirrors the workbench's `src/control/policyFile.ts` REMOTE_TRANSPORTS: `companion` is the same trust class as `ipc`. */
export const REMOTE_TRANSPORTS = ["ipc", "companion", "lan", "relay"];
export const remotePolicySchema = z.object({
    /** Transports that may be enabled at all. The default lists every one: no ceiling. */
    allowTransports: z.array(z.enum(REMOTE_TRANSPORTS)).default(["ipc", "lan", "relay"]),
    /** Scopes a remote client may hold, at most. */
    maxScopes: z.array(z.string()).optional(),
    idleTimeoutMinutes: z.number().int().min(1).max(24 * 60).default(60),
    /** The LAN listener must use TLS: plain HTTP is refused whatever `remote.lan.allowPlainHttp` says. */
    requireTls: z.boolean().optional(),
    /** Remote control stays off: the kill switch is on and cannot be lifted while this policy applies. */
    killSwitchLockedOn: z.boolean().optional(),
});
/**
 * Agent containment and egress. AIOE-first shapes from the agent-nodes kickoff (phase 2.3b); the
 * workbench's policyFile.ts does not have them yet, and ignores unknown fields until it does.
 */
export const agentsPolicySchema = z.object({
    /** Run agents in a node's sandbox: never, when one is assigned, or always (refuse otherwise). */
    sandbox: z.enum(["off", "preferred", "required"]).default("off"),
});
/**
 * Where agents may connect. The simple form is two lists of hosts and what happens to the rest.
 * `rules` say more: which programs may reach which endpoints, and for an HTTP or MCP endpoint
 * which requests or tools. `enforcement` is the rollout lever: `audit` records a breach and lets
 * it through, `enforce` blocks it. Enforced where agents run in a sandbox; a machine that cannot
 * enforce it says so rather than pretending. The vocabulary follows NVIDIA OpenShell's policy
 * (Apache 2.0; design only), so a sandbox policy can be generated from this clause.
 *
 * Order of decision for one connection (see `networkDecision`): a destination that is always
 * blocked; then `denyHosts`; then the first rule with a matching endpoint (its program list and
 * its request or tool lists decide); then `allowHosts`; then `defaultAction`.
 */
export const networkEnforcementSchema = z.enum(["audit", "enforce"]);
/** A host name, exact or with a leading wildcard label: "api.github.com", "*.githubusercontent.com". */
const hostPattern = z.string().min(1).max(253);
export const networkEndpointSchema = z.object({
    host: hostPattern,
    /** Ports allowed; any port when left out. */
    ports: z.array(z.number().int().min(1).max(65535)).max(50).optional(),
    /** What is spoken there: a plain connection, or a protocol whose requests the sandbox can read. */
    protocol: z.enum(["tcp", "rest", "websocket", "graphql", "mcp", "json-rpc"]).default("tcp"),
    /** REST: the requests allowed (any when left out) and refused. `*` matches within a path segment, `**` across them. */
    allow: z.array(z.object({ method: z.string().max(10).optional(), path: z.string().max(500).optional() })).max(100).optional(),
    deny: z.array(z.object({ method: z.string().max(10).optional(), path: z.string().max(500).optional() })).max(100).optional(),
    /** MCP: the tools an agent may call there (any when `allow` is left out) and may not. */
    tools: z.object({ allow: z.array(z.string().max(200)).max(500).optional(), deny: z.array(z.string().max(200)).max(500).optional() }).optional(),
});
export const networkRuleSchema = z.object({
    name: z.string().min(1).max(100),
    endpoints: z.array(networkEndpointSchema).min(1).max(100),
    /** The programs that may use these endpoints, by name or path ("git", "/usr/bin/curl"); any program when empty. */
    programs: z.array(z.string().min(1).max(300)).max(100).default([]),
    /** This rule's own lever; the clause's when left out. */
    enforcement: networkEnforcementSchema.optional(),
});
export const networkPolicySchema = z.object({
    allowHosts: z.array(hostPattern).max(500).default([]),
    denyHosts: z.array(hostPattern).max(500).default([]),
    /** What happens to a host on neither list and in no rule. */
    defaultAction: z.enum(["allow", "deny"]).default("allow"),
    /** Whether a breach is blocked or only recorded while the policy is rolled out. */
    enforcement: networkEnforcementSchema.default("enforce"),
    rules: z.array(networkRuleSchema).max(200).default([]),
});
/**
 * Destinations a sandbox never lets an agent reach, whatever the policy says: the machine itself,
 * link-local addresses and the cloud metadata services that hand out credentials.
 */
export const NETWORK_ALWAYS_BLOCKED = ["localhost", "127.0.0.0/8", "::1", "169.254.0.0/16", "fe80::/10", "169.254.169.254", "metadata.google.internal", "metadata.azure.internal", "100.100.100.200"];
export function isAlwaysBlocked(host) {
    const h = host.toLowerCase().replace(/^\[|\]$/g, "");
    if (h === "localhost" || h.endsWith(".localhost") || h === "::1" || h === "metadata.google.internal" || h === "metadata.azure.internal" || h === "100.100.100.200")
        return true;
    if (/^127\.\d+\.\d+\.\d+$/.test(h) || /^169\.254\.\d+\.\d+$/.test(h))
        return true;
    return /^fe[89ab][0-9a-f]:/.test(h);
}
/** Whether a host matches a pattern: exact, or `*.example.com` for any name under it. Case does not matter. */
export function hostMatches(pattern, host) {
    const p = pattern.toLowerCase();
    const h = host.toLowerCase();
    if (p === "*")
        return true;
    if (p.startsWith("*."))
        return h.endsWith(p.slice(1)) && h.length > p.length - 1;
    return p === h;
}
const globToRegExp = (glob) => new RegExp(`^${glob.split("**").map((part) => part.split("*").map((x) => x.replace(/[.+?^${}()|[\]\\]/g, "\\$&")).join("[^/]*")).join(".*")}$`);
const requestMatches = (r, method, path) => (!r.method || r.method === "*" || r.method.toUpperCase() === (method ?? "").toUpperCase()) && (!r.path || globToRegExp(r.path).test(path ?? ""));
/** The one definition of what the clause means for a connection: what a sandbox enforces and what the console previews. */
export function networkDecision(policy, c) {
    const breach = (by, reason, lever = policy.enforcement) => ({ action: lever === "audit" ? "audit" : "deny", by, reason });
    if (isAlwaysBlocked(c.host))
        return { action: "deny", by: "always-blocked", reason: "the machine itself, link-local addresses and cloud metadata services are never reachable" };
    if (policy.denyHosts.some((p) => hostMatches(p, c.host)))
        return breach("denyHosts", `${c.host} is on the blocked list`);
    const program = c.program ? (c.program.split(/[\\/]/).pop() ?? c.program) : undefined;
    for (const rule of policy.rules) {
        const endpoint = rule.endpoints.find((e) => hostMatches(e.host, c.host) && (!e.ports || c.port === undefined || e.ports.includes(c.port)));
        if (!endpoint)
            continue;
        const by = `rule:${rule.name}`;
        const lever = rule.enforcement ?? policy.enforcement;
        if (rule.programs.length && !(c.program && rule.programs.some((p) => p === c.program || p === program)))
            return breach(by, `${c.program ?? "this program"} is not one of the programs this rule lets reach ${c.host}`, lever);
        if (endpoint.protocol === "mcp" && c.tool !== undefined) {
            if (endpoint.tools?.deny?.includes(c.tool))
                return breach(by, `the tool ${c.tool} is refused there`, lever);
            if (endpoint.tools?.allow && !endpoint.tools.allow.includes(c.tool))
                return breach(by, `the tool ${c.tool} is not one of the tools allowed there`, lever);
        }
        if (c.method !== undefined || c.path !== undefined) {
            if (endpoint.deny?.some((r) => requestMatches(r, c.method, c.path)))
                return breach(by, `${c.method ?? "that request"} ${c.path ?? ""} is refused there`.trim(), lever);
            if (endpoint.allow && !endpoint.allow.some((r) => requestMatches(r, c.method, c.path)))
                return breach(by, `${c.method ?? "that request"} ${c.path ?? ""} is not one of the requests allowed there`.trim(), lever);
        }
        return { action: "allow", by, reason: `${rule.name} allows it` };
    }
    if (policy.allowHosts.some((p) => hostMatches(p, c.host)))
        return { action: "allow", by: "allowHosts", reason: `${c.host} is on the allowed list` };
    return policy.defaultAction === "allow" ? { action: "allow", by: "default", reason: "other hosts are allowed" } : breach("default", "other hosts are blocked");
}
/**
 * Which teams may run on a project (T1's refusal of record). AIOE fills it per project from the
 * enterprise teams granted to it; the workbench refuses a start the clause forbids and audits
 * `team.denied { teamId, projectId, reason }`.
 */
export const teamsPolicySchema = z.object({
    /** The organisation's teams granted to the project may run on it. */
    allowGranted: z.boolean().default(true),
    /** Members may run teams and specialists of their own on it. */
    allowPersonal: z.boolean().default(true),
    /** Ids of the enterprise teams granted to the project. */
    granted: z.array(z.string().min(1).max(100)).max(500).default([]),
});
/** Why a person's policy was tightened, and what the untightened clauses were for routines (`system` and `schedule` actors). */
export const policyOverlaySchema = z.object({
    reason: z.enum(["user-risk", "device-compliance", "agent-risk"]),
    level: z.enum(["low", "medium", "high"]).optional(),
    setBy: z.string().max(200).optional(),
    /** Whose policy this is (Entra object id). */
    principal: z.string().max(200).optional(),
    at: z.string().max(64).optional(),
    /** The clauses as they were before the tightening, for routines. */
    untightened: z.object({ approvals: approvalsPolicySchema.optional() }).optional(),
});
export const workspacePolicySchema = z.object({
    version: z.literal(POLICY_VERSION).default(POLICY_VERSION),
    /** Where it came from. The platform writes `aioe:<tenant>/<policyId>@<revision>`. */
    source: z.string().optional(),
    allowedRuntimes: z.array(z.string()).optional(),
    allowedProviders: z.array(z.string()).optional(),
    inference: z.record(z.string(), z.unknown()).optional(),
    dlp: dlpPolicySchema.prefault({}),
    approvals: approvalsPolicySchema.prefault({}),
    remote: remotePolicySchema.prefault({}),
    git: z
        .object({ protectedBranches: z.array(z.string()).default([]), requirePullRequest: z.boolean().default(false) })
        .prefault({}),
    agents: agentsPolicySchema.optional(),
    network: networkPolicySchema.optional(),
    /** Which tools (MCP servers) agents may use: tools.ts. AIOE fills in `servers` from the catalogue and the reviews. */
    tools: toolsPolicySchema.optional(),
    /** What may be done with the organisation's secrets on a machine: secrets.ts. */
    secrets: secretsPolicySchema.optional(),
    /** Filled in by AIOE for a project (never stored on a policy record). */
    teams: teamsPolicySchema.optional(),
    /** The organisation's merge rules for the project, set on its page; the workbench refuses a merge these forbid. */
    pullRequests: pullRequestsPolicySchema.optional(),
    /** Cloud agents as team members (cloudAgents.ts, D184): which providers, organisation keys, human review, how many at once. A managed policy without it allows no cloud. */
    cloudAgents: cloudAgentsPolicySchema.optional(),
    /** AI image models (media.ts, D193): off, on this computer only, or allowed; which providers, how many a day, whether each waits for a yes. A managed policy without it: off. */
    media: mediaPolicySchema.optional(),
    /** Microsoft 365 Copilot calls our agents (a2a.ts): whether at all, which Copilot Studio agents, whether a person approves first. Left out of a managed policy: nothing is callable. */
    a2a: a2aPolicySchema.optional(),
    /** Governed workbench and node settings, by the workbench's own keys (settingsPolicy.ts); merged tenant then project. */
    settings: z.record(z.string(), z.unknown()).optional(),
    settingsMode: z.record(z.string(), settingsModeSchema).optional(),
    /** The Microsoft 365 integration (microsoft365.ts): what run telemetry carries, how agent identities are cut, the direct export path. */
    microsoft365: microsoft365PolicySchema.optional(),
    /**
     * Exceptions the organisation approved where the policy refused someone (workbench D379, policyExceptions.ts): filled in
     * by AIOE for a project from its approved, unexpired, unrevoked requests (never stored on a policy record).
     */
    exceptions: z.array(policyExceptionSchema).max(200).optional(),
    /** Stamped by AIOE with a per-person tightening (Agent 365 plan, C3b) so the pin stays tamper-evident; the workbench reads it to explain the tightened clauses. */
    overlay: policyOverlaySchema.optional(),
});
/** What `GET /policy/v1` answers: the effective policy for the caller plus a revision the client can send back as If-None-Match. */
export const policyResponseSchema = z.object({
    revision: z.string(),
    policy: workspacePolicySchema,
    /**
     * False when the tenant has published no policy and the answer is the defaults plus the
     * project's own clauses (teams, pullRequests): a workbench keeps the repository's own policy
     * file and applies only those clauses.
     */
    published: z.boolean().optional(),
});
//# sourceMappingURL=policy.js.map